import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { ingredientLineName } from "@/types/recipe";
import { useUoms } from "@/hooks/useUomReference";
import type { LineMeasures } from "@/lib/api/requests/recipes";
import { LineAllergens } from "../AllergenSymbol";
import { StepPhotos } from "../StepPhotos";
import { CcpLegend, CcpStepFlag } from "../ccp";
import { methodHasCcp } from "@/lib/recipes/ccp";
import { useBatchLabel } from "./useBatchLabel";
import {
  cardEquivalent,
  flowLayout,
  kitchenAmount,
  stepRangeLabel,
  type BatchScale,
  type FlowLayout,
  type MethodGroup,
  type StagedMethod,
} from "@/lib/recipes/staged-method";

interface RecipeFlowGridProps {
  method: StagedMethod;
  scale: BatchScale;
  /** The yield at this scale ("32"), or null when the recipe has none. */
  yieldCount: string | null;
  /** Each line's weight and volume, by ingredient line id, from the cost summary. */
  measures?: Map<number, LineMeasures | null>;
  /** The recipe, for the "Got it" on a critical control point. */
  recipeId?: number;
  /** The cooking session cook mode was opened from, when it was. */
  cookingSessionId?: number;
}

/**
 * 8b, the flow grid: ingredients down the side, one column per stage, and
 * each stage's block spanning every line it works on — the ones that joined at
 * it or before. Picking a block shows that stage's steps underneath.
 *
 * A method not split into stages is drawn as one column spanning every line,
 * so the cook still gets the ingredients and the steps in the same layout.
 */
export function RecipeFlowGrid({ method, scale, yieldCount, measures, recipeId, cookingSessionId }: RecipeFlowGridProps) {
  const { t } = useLingui();
  const { data: uoms } = useUoms();
  const [selected, setSelected] = useState(0);
  const { columns, rows, spans, starts, heldInto } = method.isStaged ? flowLayout(method) : unstagedLayout(method);
  const current = columns[Math.min(selected, Math.max(0, columns.length - 1))];
  // Steps no stage claims have no column; they are listed after the grid.
  const unstagedSteps = method.isStaged
    ? method.groups.filter((group) => group.stage === null).flatMap((group) => group.steps)
    : [];
  const batchLabel = useBatchLabel(scale, yieldCount);

  // Tasks and steps no stage claims are shown whether or not the grid can
  // be drawn, so a method with tasks but no stages still lists them.
  const tasksStrip =
    method.tasks.length > 0 ? (
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl bg-(--color-surface-muted) px-4 py-3 text-sm">
        <span className="text-xs font-bold tracking-wide text-(--color-text-secondary) uppercase">{t`Before you start`}</span>
        {method.tasks.map((task) => (
          <span key={task.id} className="flex items-center gap-1.5 font-medium text-(--color-text-primary)">
            <CheckSquare className="size-4 text-(--color-text-secondary)" />
            {task.action}
            {task.detail && <span className="font-normal text-(--color-text-secondary)">· {task.detail}</span>}
          </span>
        ))}
      </div>
      ) : null;
  const unstagedPanel =
    unstagedSteps.length > 0 ? (
      <div className="flex flex-col gap-2 rounded-2xl border border-dashed border-(--color-border) p-5">
        <span className="text-xs font-semibold tracking-wide text-(--color-text-secondary) uppercase">{t`Not staged yet`}</span>
        {unstagedSteps.map((step) => (
          <div key={step.number} className="flex gap-2 text-sm">
            <span className="font-bold text-(--color-text-primary)">{step.number}.</span>
            <span className="flex min-w-0 flex-col">
              {step.isCcp ? (
                <span className="flex items-start gap-2">
                  <span className="text-(--color-text-primary)">{step.text}</span>
                  <CcpStepFlag recipeId={recipeId} cookingSessionId={cookingSessionId} number={step.number} text={step.text} />
                </span>
              ) : (
                <span className="text-(--color-text-primary)">{step.text}</span>
              )}
              <StepPhotos urls={step.mediaUrls} />
            </span>
          </div>
        ))}
      </div>
      ) : null;

  if (columns.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {tasksStrip}
        <p className="rounded-xl bg-(--color-surface-muted) px-4 py-6 text-center text-sm text-(--color-text-secondary)">
          {t`This recipe has no ingredients or steps yet.`}
        </p>
        {unstagedPanel}
      </div>
    );
  }

  const stepsLabel = (numbers: number[]) => {
    const range = stepRangeLabel(numbers);
    if (!range) return null;
    if ("one" in range) return t`Step ${range.one}`;
    if ("list" in range) {
      const list = range.list.join(", ");
      return t`Steps ${list}`;
    }
    return t`Steps ${range.from}–${range.to}`;
  };

  return (
    <div className="flex flex-col gap-4">
      {tasksStrip}
      {/* Every cell is ruled by the 1px gap over the grid's dark background,
          inside a heavier outer rule, as the design draws it. */}
      <div className="overflow-x-auto rounded-xl border-[1.5px] border-(--color-text-primary) print:overflow-visible">
        <div
          className="grid min-w-[52rem] gap-px bg-(--color-text-primary) text-base"
          style={{
            gridTemplateColumns: `minmax(16rem, 1fr) repeat(${columns.length}, 7.75rem)`,
            gridTemplateRows: `2.5rem repeat(${Math.max(rows.length, 1)}, minmax(2.25rem, auto))`,
          }}
        >
          <div className="flex items-center bg-(--color-surface-muted) px-3.5 text-[13px] font-semibold tracking-wide text-(--color-text-secondary) uppercase">
            {t`Ingredients`} · {batchLabel}
          </div>
          {columns.map((group, index) => (
            <div
              key={index}
              className="flex items-center justify-center bg-(--color-surface-muted) px-3 text-[13px] font-semibold tracking-wide text-(--color-text-secondary) uppercase"
            >
              {group.stage ? t`Stage ${index + 1}` : t`Method`}
            </div>
          ))}

          {rows.length === 0 && (
            // A method with steps and no lines still has a row for its block,
            // and the cell beside it would otherwise show the grid's rule colour.
            <div style={{ gridColumn: 1, gridRow: 2 }} className="bg-(--color-surface-content)" />
          )}
          {rows.map((row, index) => {
            const equivalent = cardEquivalent(Number(row.line.quantity), row.line.unit, measures?.get(row.line.id), scale, uoms);
            return (
              <div
                key={row.line.id}
                style={{ gridColumn: 1, gridRow: index + 2 }}
                className="flex items-center justify-between gap-3 bg-(--color-surface-content) px-3.5 py-1.5"
              >
                <span className="min-w-0">
                  <span className="font-semibold text-(--color-text-primary)">{ingredientLineName(row.line)}</span>
                  {row.line.preparationNotes && (
                    <span className="text-(--color-text-secondary)"> {row.line.preparationNotes}</span>
                  )}{" "}
                  <LineAllergens line={row.line} />
                </span>
                <span className="flex shrink-0 flex-col items-end text-right">
                  <span className="font-bold whitespace-nowrap text-(--color-text-primary)">
                    {kitchenAmount(Number(row.line.quantity) * scale, row.line.unit)}
                  </span>
                  {equivalent && <span className="text-xs text-(--color-text-secondary)">{equivalent}</span>}
                </span>
              </div>
            );
          })}

          {columns.map((group, index) => {
            const isSelected = current === group;
            const span = spans[index]!;
            const start = starts[index]!;
            const held = heldInto[index]!;
            const label = stepsLabel(group.steps.map((step) => step.number));
            const action = group.stage?.action ?? t`All steps`;
            const detail = group.stage?.detail;
            const flagged = group.steps.filter((step) => step.isCcp);
            const cell = { gridColumn: index + 2, gridRow: `${start + 2} / ${start + span + 2}` };
            const blockButton = (
                <button
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelected(index)}
                  // With a warning icon over it, the button sits in a wrapper that takes the grid cell.
                  style={flagged.length > 0 ? undefined : cell}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 p-2 text-center transition-colors",
                    isSelected
                      ? "bg-(--color-surface-sidebar) text-(--color-text-on-dark)"
                      : "bg-(--color-surface-content) text-(--color-text-primary) hover:bg-(--color-surface-muted)",
                  )}
                >
                  <span className="text-lg leading-tight font-bold">{action}</span>
                  {detail && (
                    <span className={cn("text-sm leading-snug", isSelected ? "text-(--color-text-on-dark)/75" : "text-(--color-text-secondary)")}>
                      {detail}
                    </span>
                  )}
                  {held.map((inflow) => (
                    <span
                      key={inflow.stage.id}
                      className={cn("text-xs font-semibold", isSelected ? "text-(--color-text-on-dark)/75" : "text-(--color-text-secondary)")}
                    >
                      {t`Add when ready: ${inflow.stage.action}`}
                    </span>
                  ))}
                  {label && (
                    <span className={cn("mt-1 text-xs font-semibold tracking-wide uppercase", isSelected ? "text-(--color-text-on-dark)/75" : "text-(--color-text-secondary)")}>
                      {label}
                    </span>
                  )}
                </button>
            );
            return (
              <div key={group.stage?.id ?? "unstaged"} className="contents">
                {flagged.length > 0 ? (
                  // The icon is a sibling of the block's button, never inside it: a button in a button is not valid.
                  <div style={cell} className="relative grid">
                    {blockButton}
                    <div className="absolute top-1 right-1 flex gap-1">
                      {flagged.map((step) => (
                        <CcpStepFlag
                          key={step.number}
                          recipeId={recipeId}
                          cookingSessionId={cookingSessionId}
                          number={step.number}
                          text={step.text}
                          stageName={group.stage?.action}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  blockButton
                )}
                {start > 0 && (
                  <div
                    aria-hidden
                    style={{ gridColumn: index + 2, gridRow: `2 / ${start + 2}` }}
                    className="bg-(--color-surface-muted)"
                  />
                )}
                {start + span < rows.length && (
                  <div
                    aria-hidden
                    style={{ gridColumn: index + 2, gridRow: `${start + span + 2} / ${rows.length + 2}` }}
                    className="bg-(--color-surface-muted)"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {current && (
        <div className="grid grid-cols-1 gap-6 rounded-2xl bg-(--color-surface-muted) px-6 py-5 md:grid-cols-[12.5rem_minmax(0,1fr)]">
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-bold tracking-wider text-(--color-text-secondary) uppercase">
              {current.stage ? t`Stage ${columns.indexOf(current) + 1} of ${columns.length}` : t`Not staged yet`}
            </span>
            <span className="text-[28px] leading-tight font-semibold text-(--color-text-primary)">{current.stage?.action ?? t`All steps`}</span>
            {current.stage?.detail && <span className="text-[15px] text-(--color-text-secondary)">{current.stage.detail}</span>}
          </div>
          <div className="flex flex-col gap-2.5">
            {current.steps.length === 0 ? (
              <span className="text-sm text-(--color-text-secondary)">{t`No steps linked to this stage.`}</span>
            ) : (
              current.steps.map((step) => (
                <div key={step.number} className="grid grid-cols-[1.75rem_minmax(0,1fr)] text-[17px] leading-relaxed">
                  <span className="font-bold text-(--color-text-primary)">{step.number}.</span>
                  <span className="flex min-w-0 flex-col">
                    {step.isCcp ? (
                      <span className="flex items-start gap-2">
                        <span className="text-(--color-text-primary)">{step.text}</span>
                        <CcpStepFlag
                          recipeId={recipeId}
                          cookingSessionId={cookingSessionId}
                          number={step.number}
                          text={step.text}
                          stageName={current.stage?.action}
                          className="mt-1"
                        />
                      </span>
                    ) : (
                      <span className="text-(--color-text-primary)">{step.text}</span>
                    )}
                    <StepPhotos urls={step.mediaUrls} />
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {methodHasCcp(method) && <CcpLegend />}
      {unstagedPanel}
    </div>
  );
}

/**
 * The flow layout for a method with no stages: its one group as a single
 * column over every line. A method with no lines and no steps has no column.
 */
function unstagedLayout(method: StagedMethod): FlowLayout {
  const group: MethodGroup | undefined = method.groups[0];
  if (!group || (group.lines.length === 0 && group.steps.length === 0)) {
    return { columns: [], rows: [], spans: [], starts: [], heldInto: [] };
  }
  return {
    columns: [group],
    rows: group.lines.map((line) => ({ line, joinsAt: 0 })),
    spans: [Math.max(1, group.lines.length)],
    starts: [0],
    heldInto: [[]],
  };
}
